import { useContext } from "react";
import { Box, Typography } from "@mui/material";
import type { EChartsOption } from "echarts";
import { RopChart } from "../../../../main/components/RopChart";
import { MainContext } from "../../../../main/context/MainContextProvider";
import { RootCauseAnalysisData } from "../../types";

export type AnaliseCausaRaizCascataChartProps = {
    data: RootCauseAnalysisData;
    height?: number | string;
};

export default function AnaliseCausaRaizCascataChart({ data, height }: AnaliseCausaRaizCascataChartProps) {
    const { theme } = useContext(MainContext);

    // Use ideal energy and generated energy from data
    const idealEnergy = data.idealEnergy;
    const realEnergy = data.generatedEnergy;

    // Criar categorias de perdas para o gráfico de cascata
    const categories = [
        {
            name: "Perdas Ópticas/\nIrradiância",
            value: data.opticalAndIrradiationLosses
        },
        {
            name: "Perdas DC\n(Mismatch/Cabos) ",
            value: data.conversionAndDCModuleLosses
        },
        {
            name: "Perdas nos\nInversores",
            value: data.inverterEfficiencyLosses
        },
        {
            name: "Perdas no\ntrafo ",
            value: data.transformerEfficiencyLosses
        },
        {
            name: "Indisponibilidade\nInv.",
            value: data.inverterUnavailabilityLoss
        },
        {
            name: "Indisponibilidade\nString",
            value: data.stringUnavailabilityLoss
        },
        {
            //TODO: a implementação acima foi realizada para a release de dia 09.04.26, por dados estranhos gerados a partir da implementaão da regra de outros ganhos / outras perdas, necessário realizar validação novamente

            name: data.energyBalance < -0.000001 ? "Outros\nGanhos" : "Outras\nPerdas",
            value: data.energyBalance
        }
    ];


    // Ordem de exibição no eixo X
    const ordemEixoXGrafico = [
        "Energia\nTeórica",
        ...categories.map(cat => cat.name),
        "Energia\nGerada\nReal"
    ];

    // Dados para energia (apenas primeira e última barra)
    const energiaEixoYGrafico = [
        idealEnergy,
        ...categories.map(() => "-"),
        realEnergy
    ];

    // Dados para perdas (barras intermediárias)
    const perdasEixoYGrafico = [
        "-",
        ...categories.map(cat => {
            const catValue = cat.value ?? 0;
            // Para ganhos (valores negativos), retornamos o valor absoluto
            // pois a lógica da cascata já está invertida no placeholder
            return Math.abs(catValue);
        }),
        "-"
    ];

    // Calcular os valores acumulados para o placeholder (cria efeito cascata)
    let valorAtual = idealEnergy;
    const dadosEixoYDiferencaGrafico = [
        0, // Energia Ideal sempre 0
        ...categories.map(cat => {
            const catValue = cat.value ?? 0;
            if (catValue < 0) {
                // É um ganho - a barra deve começar do valor atual e subir
                const inicio = valorAtual;
                valorAtual -= catValue; // Subtrai o negativo (ou seja, soma)
                return inicio;
            } else {
                // É uma perda - a barra deve começar do próximo valor e descer
                valorAtual -= catValue;
                return valorAtual;
            }
        }),
        0 // Energia Gerada sempre 0
    ];

    const tooltipDescriptions: Record<number, string> = {
        0: "Energia máxima estimada com base na irradiância disponível e nos parâmetros de projeto da usina, sem considerar perdas do sistema.",
        1: "Perdas associadas à incidência da radiação nos módulos, incluindo efeitos ópticos, sujeira (soiling), ângulo de incidência e variações de irradiância.",
        2: "Perdas no lado DC causadas por diferen\u00e7as el\u00e9tricas entre m\u00f3dulos (mismatch), resist\u00eancias em cabos, conex\u00f5es e pequenas varia\u00e7\u00f5es entre strings.",
        3: "Perdas decorrentes da convers\u00e3o de corrente cont\u00ednua (DC) para corrente alternada (AC) nos inversores devido \u00e0 efici\u00eancia de convers\u00e3o.",
        4: "Perdas el\u00e9tricas associadas \u00e0 transforma\u00e7\u00e3o de tens\u00e3o nos transformadores, incluindo perdas em cobre e no n\u00facleo magn\u00e9tico.",
        5: "Energia n\u00e3o gerada devido a per\u00edodos em que inversores estiveram indispon\u00edveis por falhas, prote\u00e7\u00e3o, manuten\u00e7\u00e3o ou desligamento operacional.",
        6: "Perdas causadas por strings ou conjuntos de m\u00f3dulos fora de opera\u00e7\u00e3o devido a falhas, desconex\u00f5es ou problemas no campo fotovoltaico.",
        7: "Ganhos ou perdas de gera\u00e7\u00e3o decorrentes de condi\u00e7\u00f5es operacionais n\u00e3o diagnosticadas.",
        8: "Energia efetivamente produzida e entregue pela usina ap\u00f3s a considera\u00e7\u00e3o de todas as perdas ou ganhos do sistema.",
    };

    const chartOption: EChartsOption = {
        backgroundColor: "transparent",
        tooltip: {
            trigger: "axis",
            axisPointer: {
                type: "shadow"
            },
            formatter: (params: any) => {
                const tar1 = params[1]; // Energia
                const tar2 = params[2]; // Perdas
                const dataIndex = tar1?.dataIndex ?? tar2?.dataIndex ?? 0;
                const description = tooltipDescriptions[dataIndex] ?? "";
                const descriptionHtml = description
                    ? `<div style="margin-top:6px;font-size:11px;color:#9CA3AF;max-width:220px;white-space:normal;">${description}</div>`
                    : "";

                if (tar1?.value !== "-") {
                    const tar1ValuePtBr = typeof tar1.value === "number"
                        ? tar1.value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : tar1.value;
                    return `<strong>${tar1.name.replace(/\n/g, " ")}</strong><br/>${tar1.seriesName}: <strong>${tar1ValuePtBr} MWh</strong>${descriptionHtml}`;
                } else if (tar2?.value !== "-") {
                    const tar2ValuePtBr = typeof tar2.value === "number"
                        ? tar2.value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : tar2.value;
                    const isGain = tar2.dataIndex === 7 && tar2.name.includes("Ganhos");
                    const label = isGain ? "Ganhos" : "Perdas";
                    const sign = isGain ? "+" : "-";
                    return `<strong>${tar2.name.replace(/\n/g, " ")}</strong><br/>${label}: <strong>${sign}${tar2ValuePtBr} MWh</strong>${descriptionHtml}`;
                }
                return "";
            },
            backgroundColor: theme?.cardBoxBackgroudColor || "#1F2937",
            borderColor: theme?.cardBorderColor || "#374151",
            textStyle: {
                color: theme?.primaryTextColor || "#F9FAFB"
            }
        },
        grid: {
            left: "3%",
            right: "3%",
            bottom: "0%",
            top: "9%",
            containLabel: true,
        },
        xAxis: {
            type: "category",
            splitLine: { show: false },
            data: ordemEixoXGrafico,
            axisLine: {
                lineStyle: {
                    color: theme?.cardBorderColor || "#E5E7EB",
                },
            },
            axisLabel: {
                color: theme?.secondaryTextColor || "#6B7280",
                fontSize: 10,
                interval: 0,
                rotate: 0
            },
            axisTick: {
                show: false
            }
        },
        yAxis: {
            type: "value",
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: {
                color: theme?.secondaryTextColor || "#6B7280",
                fontSize: 11
            },
            splitLine: {
                show: true,
                lineStyle: {
                    color: theme?.cardBorderColor || "#374151",
                    type: "dashed",
                    opacity: 0.5
                }
            }
        },
        series: [
            {
                name: "Placeholder",
                type: "bar",
                stack: "Total",
                itemStyle: {
                    borderColor: "transparent",
                    color: "transparent"
                },
                emphasis: {
                    itemStyle: {
                        color: (params: any) => {
                            if (params.dataIndex === 7 && params.name.includes("Ganhos")) return "#10B981";
                            else return "#EF4444"
                        }
                    }
                },
                data: dadosEixoYDiferencaGrafico
            },
            {
                name: "Energia",
                type: "bar",
                stack: "Total",
                itemStyle: {
                    color: (params: any) => {
                        if (params.dataIndex === 0) return "#9CA3AF"; // Energia Teórica → cinza
                        return "#34D399"; // Energia Gerada Real → verde
                    }
                },
                label: {
                    show: true,
                    position: "inside",
                    color: theme?.primaryTextColor || "#F9FAFB",
                    fontSize: 10,
                    formatter: (params: any) => {
                        return typeof params.value === "number"
                            ? params.value.toLocaleString("pt-BR", {
                                minimumFractionDigits: 1,
                                maximumFractionDigits: 1
                            })
                            : `${params.value} MWh`;
                    }
                },
                data: energiaEixoYGrafico
            },
            {
                name: "Perdas",
                type: "bar",
                stack: "Total",
                itemStyle: {
                    color: (params: any) => {
                        if (params.dataIndex === 7 && params.name.includes("Ganhos")) return "#10B981"; // ganho → verde
                        if (params.dataIndex >= 1 && params.dataIndex <= 4) return "#60A5FA"; // conversão → azul
                        return "#f59e0b"; // operacional → laranja
                    }
                },
                label: {
                    show: true,
                    position: "inside",
                    color: theme?.primaryTextColor || "#F9FAFB",
                    fontSize: 10,
                    formatter: (params: any) => {
                        return typeof params.value === "number"
                            ? (params.dataIndex === 7 && params.name.includes("Ganhos") ? "" : "-") + params.value.toLocaleString("pt-BR", {
                                minimumFractionDigits: 1,
                                maximumFractionDigits: 1
                            })
                            : `${params.value} MWh`;
                    }
                },
                data: perdasEixoYGrafico
            }
        ]
    };

    return (
        <Box
            sx={{
                backgroundColor: theme.cardBoxBackgroudColor,
                borderRadius: "8px",
                padding: "16px",
                height: height || 350,
                display: "flex",
                flexDirection: "column"
            }}
        >
            <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", paddingX: "25px" }}>
                <Typography sx={{ fontSize: "14px", fontWeight: "500", marginBottom: "8px" }}>
                    Cascata de Perdas
                </Typography>
                <Typography sx={{ fontSize: "12px", color: theme.secondaryTextColor, marginBottom: "16px" }}>
                    Energia teórica → real
                </Typography>
            </Box>
            <Box sx={{ flexGrow: 1, minHeight: "200px" }}>
                <RopChart option={chartOption} />
            </Box>

            {/* Legend */}
            <Box sx={{ display: "flex", justifyContent: "center", gap: "16px", marginTop: "16px", flexWrap: "wrap" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <Box sx={{ width: "10px", height: "10px", backgroundColor: "#9CA3AF", borderRadius: "2px" }} />
                    <Typography sx={{ fontSize: "12px" }}>Energia Teórica</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <Box sx={{ width: "10px", height: "10px", backgroundColor: "#34D399", borderRadius: "2px" }} />
                    <Typography sx={{ fontSize: "12px" }}>Energia Gerada</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <Box sx={{ width: "10px", height: "10px", backgroundColor: "#60A5FA", borderRadius: "2px" }} />
                    <Typography sx={{ fontSize: "12px" }}>Perdas Previstas</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <Box sx={{ width: "10px", height: "10px", backgroundColor: "#f59e0b", borderRadius: "2px" }} />
                    <Typography sx={{ fontSize: "12px" }}>Perdas Gerenciáveis</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <Box sx={{ width: "10px", height: "10px", backgroundColor: "#10B981", borderRadius: "2px" }} />
                    <Typography sx={{ fontSize: "12px" }}>Ganhos</Typography>
                </Box>
            </Box>
        </Box>
    );
}
